<script lang="ts">
    import type { PageData } from './$types';
    export let data: PageData;
    import { afterNavigate } from '$app/navigation';
    import Background2024 from '$src/lib/components/svg/Background2024.svelte';
    import GlobeAlt from '$src/lib/components/svg/GlobeAlt.svelte';
    import ArrowTopRightOnSquare from '$src/lib/components/svg/ArrowTopRightOnSquare.svelte';

    let top: HTMLElement;

    afterNavigate(() => {
        top.scrollIntoView();
    });

    const company = data.company;
    const id = data.id;
    const token = data.token;
    const languageLink = `/2024/no/${id}`;
</script>

<svelte:head>
    <meta
        property="og:image"
        content={`https://og-images.factlines.com/api/og/2024?companyName=${company}&token=${token}`}
    />
    <meta
        property="description"
        content={`Factlines confirms that ${company} actively works to protect fundamental human rights and decent working conditions in its own enterprise and supply chain.`}
    />
    <meta
        property="og:description"
        content={`Factlines confirms that ${company} actively works to protect fundamental human rights and decent working conditions in its own enterprise and supply chain.`}
    />
    <meta property="og:title" content="Verification badge for The Norwegian Transparency Act" />
    <meta property="og:image:width" content="1200" />
    <meta property="og:image:height" content="600" />
    <meta property="og:url" content={`https://verified.factlines.com/2024/no/${id}/en`} />
</svelte:head>

<Background2024 />
<span bind:this={top} />
<div id="main" class="relative z-2 flex flex-row min-h-[100vh]">
    <article class="verified-article flex flex-col max-w-7xl mx-auto pl-20 pr-20 print:text-base">
        <div class="pt-16 pb-16 flex flex-row justify-between">
            <div>
                <img src="/logo-2024.png" style="width: 12.3rem" alt="Factlines" />
            </div>
            <div class="flex">
                <a
                    href={languageLink}
                    class="flex flex-row rounded-lg border-aubergine mr-4 pl-4 pr-4 pt-2 pb-2 items-center"
                    style="border-width: 1px"
                >
                    <GlobeAlt />
                    <span class="font-sans text-sm pl-3">Norsk</span>
                </a>
                <a
                    href="https://factlines.com/"
                    target="_blank"
                    class="flex flex-row rounded-lg border-aubergine pl-3 pr-4 pt-2 pb-2 items-center"
                    style="border-width: 1px"
                >
                    <ArrowTopRightOnSquare />
                    <span class="font-sans text-sm pl-3">Visit Factlines</span>
                </a>
            </div>
        </div>
        <header class="pt-6">
            <h3 class="text-5xl font-medium leading-loose text-aubergine">Our work with the</h3>
            <h3 class="text-5xl text-aubergine"><strong>Norwegian Transparency Act</strong></h3>
            <p class="pt-8 text-p2 font-sans">
                Factlines confirms that <strong>{data.company}</strong>, with organization number
                <strong>{data.id}</strong>, actively works to protect fundamental human rights and
                decent working conditions in its own enterprise and supply chain.
            </p>
        </header>
        <img src="/verified-2024.png" class="w-1/2" alt="Verified by Factlines 2024" />
        <section class="relative pt-24">
            <h3 class="text-4xl font-medium">Mapping of suppliers</h3>
            <p class="font-sans text-p2">
                Every year we collect information from our suppliers through the Factlines'
                software. The self-assessment is based on the UN Global Compact and the OECD's
                guidelines for responsible business conduct and aligns with the due diligence
                assessment process required by the Norwegian Transparency Act.
            </p>
        </section>
        <section class="relative font-sans bg-[#E1CDE2] rounded-xl p-8">
            <p class="text-p2">The self-assessment includes the following topics:</p>
            <ul class="ml-6">
                <li class="text-p2">CSR - strategy and guidelines</li>
                <li class="text-p2">Supply chain: follow-up, insight and control</li>
                <li class="text-p2">Risk assessment and Due Diligence procedures</li>
                <li class="text-p2">Country risk</li>
                <li class="text-p2">Corporate management systems</li>
                <li class="text-p2">Labour rights</li>
                <li class="text-p2">People and environment</li>
                <li class="text-p2">Conflict minerals</li>
                <li class="text-p2">Anti-bribery and corruption</li>
            </ul>
        </section>
        <section class="relative font-sans pt-4">
            <p class="text-p2">
                The data collection provides a basis for evaluating whether a good practice has been
                established to comply with the Norwegian Transparency Act, the Norwegian Public
                Procurement Act Section 5, and the enterprise's own contract provisions.
            </p>
        </section>
        <section class="relative font-sans">
            <p class="text-p2">
                The mapping applies to direct suppliers and producers/production sites and gathers
                information about their routines and procedures to follow up the entire supply
                chain.
            </p>
        </section>
        <section class="relative font-sans">
            <p class="text-p2">
                The information on risk in production countries is based on several sources,
                including ITUC (The International Trade Union Confederation) on labor rights and
                Transparency International (corruption index).
            </p>
        </section>
        <section class="relative font-sans">
            <p class="text-p2">
                Furthermore, matters surrounding employment rights, anti-corruption, and whether the
                suppliers have carried out self-reporting, improvement projects and/or inspections
                at the production site are surveyed. It also examines whether the suppliers have
                established routines for, among other things, quality, health and safety, the
                environment, and social responsibility.
            </p>
        </section>
        <section class="relative font-sans">
            <p class="text-p2">
                We inform our suppliers about our ethical guidelines and require that they deliver
                goods and services produced in line with these guidelines. They are also required to
                communicate and follow up the guidelines with their subcontractors.
            </p>
        </section>
        <section class="relative font-sans pb-8">
            <p class="text-p2">
                If the self-assessment or other indicators indicate deviations, these are followed
                up with the supplier in question. The purpose is to prevent and reduce adverse
                impacts in cooperation with the supplier and other participants in the supply chain.
            </p>
        </section>
    </article>
    <article class="w-1/6"></article>
</div>
<div id="main" class="relative z-2 flex flex-row bg-[#E1CDE2]">
    <article class="verified-article flex flex-col max-w-7xl mx-auto pl-20 pr-20 print:text-base">
        <section class="relative flex items-center gap-4 pt-16">
            <img src="/siri-2024.png" alt="Siri Engesæth" class="w-24" />
            <span class="text-lg font-sans"
                ><span class="text-2xl">Siri Engesæth</span><br />Chief Sustainability &
                Communications Officer</span
            >
        </section>
        <div class="pb-8">
            <a href="https://factlines.com/" target="_blank"
                ><img
                    src="/logo-2024.png"
                    class="pt-16"
                    style="width: 12.3rem"
                    alt="Factlines"
                /></a
            >
        </div>
        <p class="font-sans pb-16 text-p2">
            Factlines is a technology company that develops user-friendly ESG software. Our digital
            tools makes it easier to manage supplier relationships and implement measures for a more
            fair future. Everything in line with key European and Norwegian regulations.
        </p>
    </article>
    <article class="w-1/6"></article>
</div>
